/**
 * When something is for: a **day** for a todo, an **instant** for a reminder
 * (D260814i, D260814m).
 *
 * A due date is a day. The server stores `2026-08-14` and arbitrates no
 * timezone; "today" is therefore a question only the browser in front of
 * somebody can answer, and it answers it with the local calendar. A todo due on
 * the 14th is due on the 14th in Paris and in Montréal, which is what a person
 * means by a due date.
 *
 * A reminder is a moment, and the server owns it — it has to, since it is the
 * one comparing it against a clock. What this module does with an instant is
 * turn it back into the day and the time a person reads, which is `dayOf` plus
 * the local formatter: past that point every predicate here is the day one.
 *
 * Everything is pure and takes `now` as an argument, so the interesting cases —
 * the minute either side of midnight, the last day of a month — are testable
 * without waiting for them.
 */

import type { Translate } from './i18n/translate';

/** Today in the browser's own calendar, as `YYYY-MM-DD`. */
export function todayISO(now: Date = new Date()): string {
  const year = String(now.getFullYear()).padStart(4, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function isToday(dueOn: string, now: Date = new Date()): boolean {
  return dueOn === todayISO(now);
}

/**
 * Past, not merely dated. A string comparison rather than two `Date` objects:
 * `YYYY-MM-DD` sorts chronologically as text, which is the whole reason the column
 * has that shape.
 */
export function isOverdue(dueOn: string, now: Date = new Date()): boolean {
  return dueOn < todayISO(now);
}

/**
 * A due day, written the way it would be said.
 *
 * The three days either side of today are named rather than dated — "Tomorrow" is
 * read faster than "15 Aug", and those are the ones a list is mostly made of. The
 * rest are formatted by `Intl` in the language in force, with the year added only
 * when it is not the current one: a list where every row carries "2026" makes the
 * one row from 2027 harder to spot, not easier.
 */
export function formatDue(dueOn: string, t: Translate, now: Date = new Date()): string {
  const today = todayISO(now);
  if (dueOn === today) return t('todos.dueToday');
  if (dueOn === shiftDays(today, 1)) return t('todos.dueTomorrow');
  if (dueOn === shiftDays(today, -1)) return t('todos.dueYesterday');

  return writeDay(dueOn, t, today);
}

/* ------------------------------------------------------------------ Instants */

/**
 * The **local calendar day** an instant falls on.
 *
 * This is the whole bridge between the two halves of this module: past it, a
 * reminder is a day like any other and every predicate above applies to it. It
 * has to be the local day and not the UTC one — 23:30 in Paris on the 14th is
 * the 14th to the person reading it, whatever the `Z` in the stored value says.
 */
export function dayOf(instant: string): string {
  return todayISO(new Date(instant));
}

/**
 * The moment has passed.
 *
 * Compared as numbers rather than as text, which is the difference from
 * `isOverdue`: two instants may be spelt identically and this is still about a
 * minute rather than a day. A reminder for 14:30 is late at 14:31, not at
 * midnight.
 */
export function isPast(instant: string, now: Date = new Date()): boolean {
  return new Date(instant).getTime() <= now.getTime();
}

/**
 * A moment, written the way it would be said: "Today 14:30", "Tomorrow 09:00",
 * "15 Aug 09:00".
 *
 * Same rule as `formatDue` for the day — the three a list is mostly made of are
 * named rather than dated — with the time always beside it, because a reminder
 * with no time on screen is indistinguishable from a todo.
 *
 * `hourCycle: 'h23'` rather than the locale's own preference. An English reader
 * would otherwise get "02:30 PM", which is three characters wider than every
 * French row in the same right-aligned column, and turns midnight into "12:00
 * AM" — the one time of day where the twelve-hour clock is actively confusing.
 */
export function formatRemindAt(instant: string, t: Translate, now: Date = new Date()): string {
  const moment = new Date(instant);
  const day = dayOf(instant);
  const today = todayISO(now);
  const time = new Intl.DateTimeFormat(t.locale, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(moment);

  if (day === today) return t('reminders.atToday', time);
  if (day === shiftDays(today, 1)) return t('reminders.atTomorrow', time);
  if (day === shiftDays(today, -1)) return t('reminders.atYesterday', time);

  return t('reminders.atDay', writeDay(day, t, today), time);
}

/**
 * An instant as a `datetime-local` field wants it: the local calendar, to the
 * minute, with no offset — `2026-08-14T14:30`.
 *
 * Not `toISOString().slice(0, 16)`, which is the same shape holding a different
 * time: that one is UTC, and every browser east or west of Greenwich would open
 * the field on a moment nobody chose.
 */
export function toLocalInput(instant: string): string {
  const moment = new Date(instant);
  const hours = String(moment.getHours()).padStart(2, '0');
  const minutes = String(moment.getMinutes()).padStart(2, '0');
  return `${todayISO(moment)}T${hours}:${minutes}`;
}

/**
 * The instant a `datetime-local` value names, or `null` when it names none.
 *
 * **This and `todayISO` are the only places the browser's timezone is read.** A
 * `datetime-local` gives back what somebody typed, in their own calendar and
 * with no offset at all; interpreting it locally is the one reading that matches
 * what they meant, and from here on it is an instant like any other.
 */
export function fromLocalInput(local: string): string | null {
  const moment = new Date(local);
  return Number.isNaN(moment.getTime()) ? null : moment.toISOString();
}

/**
 * A day, dated rather than named, in the language in force.
 *
 * The year is added only when it is not the current one: a list where every row
 * carries "2026" makes the one row from 2027 harder to spot, not easier. Written
 * once because both a due date and a reminder's day go through it, and two
 * copies would eventually disagree about the year rule.
 */
function writeDay(day: string, t: Translate, today: string): string {
  return new Intl.DateTimeFormat(t.locale, {
    day: 'numeric',
    month: 'short',
    ...(day.slice(0, 4) === today.slice(0, 4) ? {} : { year: 'numeric' }),
    // The day is anchored at UTC midnight below, so the formatter has to read it
    // there too. Left to the local zone, anybody west of Greenwich would see every
    // date shifted a day earlier.
    timeZone: 'UTC',
  }).format(asDate(day));
}

/** A day at UTC midnight, which is the only instant this module ever needs. */
function asDate(day: string): Date {
  return new Date(`${day}T00:00:00.000Z`);
}

/**
 * The day before or after another one.
 *
 * Done in UTC so it is plain arithmetic: adding a day across a daylight-saving
 * boundary in local time either repeats a day or skips one, and a list would name
 * the wrong one twice a year.
 */
function shiftDays(day: string, days: number): string {
  const date = asDate(day);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}
